import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { apiClient } from '../services/api';
import { ConfirmButton } from './ConfirmButton';

/** `GET /bidding/bookmarks` 의 한 줄 */
interface BookmarkRow {
  id: number;
  bidNtceNo: string;
  bidNtceNm: string;
  clientName: string;
  snapshot: {
    closingAt?: string;
    closingKind?: string;
    presmptPrce?: string;
    bidNtceDtlUrl?: string;
    attachments?: { url: string; name?: string }[];
    [k: string]: unknown;
  } | null;
  memo: string | null;
  createdAt: string;
  createdBy: string | null;
  /** 공고가 아직 DB 에 있으면 원본. 마감돼 지워졌으면 null */
  bid: Record<string, unknown> | null;
  hasSummary: boolean;
}

const fmtDate = (iso?: string) => {
  if (!iso) return '-';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * 공고가 마감돼 DB 에서 지워졌으면 담을 때 베껴 둔 것으로 상세를 연다.
 * `_snapshotOnly` — 상세에서 담기 버튼을 감춘다(원본이 없어 담을 수 없다).
 */
function fromSnapshot(r: BookmarkRow): Record<string, unknown> {
  const { attachments = [], closingAt, closingKind, ...rest } = r.snapshot ?? {};
  const at = closingAt ? `${fmtDate(closingAt)}:00` : '';
  return {
    ...rest,
    bidNtceNo: r.bidNtceNo,
    bidNtceNm: r.bidNtceNm,
    ntceInsttNm: rest.ntceInsttNm ?? r.clientName,
    [closingKind === '개찰' ? 'opengDt' : 'bidClseDt']: at,
    ...Object.fromEntries(
      attachments.slice(0, 10).flatMap((a, i) => [
        [`ntceSpecDocUrl${i + 1}`, a.url],
        [`ntceSpecFileNm${i + 1}`, a.name],
      ]),
    ),
    _snapshotOnly: true,
  };
}

const fmtPrice = (v?: string) => {
  const n = parseInt(v ?? '', 10);
  return n ? `${n.toLocaleString('ko-KR')}원` : '-';
};

/**
 * 공고 화면의 「북마크」 탭. 팀이 「나중에 볼 것」으로 찍은 공고를 마감 가까운 순으로 모아 본다.
 *
 * 프로젝트 칸반은 검토부터라 북마크가 보이지 않는다. 공고 목록의 ★ 는 그 공고를
 * 찾아야만 보이고, 마감되면 공고와 함께 사라진다 — 그래서 따로 둔다(2026-09-30).
 * 마감 지난 것도 남긴다. 「미참여」로 치우는 것은 사람이 한다.
 */
export const BidBookmarkList: React.FC<{
  onOpen: (bid: Record<string, unknown>) => void;
  onChanged: () => void;
}> = ({ onOpen, onChanged }) => {
  const [rows, setRows] = useState<BookmarkRow[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setRows(await apiClient.get('/bidding/bookmarks'));
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (row: BookmarkRow, path: string, method: 'POST' | 'DELETE' = 'POST') => {
    setBusy(row.id);
    setError('');
    try {
      if (method === 'DELETE') await apiClient.delete(path);
      else await apiClient.post(path, {});
      await load();
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (!rows) {
    return error ? (
      <p className="text-sm text-red-600">{error}</p>
    ) : (
      <p className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> 불러오는 중…</p>
    );
  }

  return (
    <div>
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>}
      <p className="text-sm text-slate-500 mb-4">
        총 <span className="font-semibold text-slate-900">{rows.length}</span>건 · 마감 가까운 순 · 팀 전체가 같이 봅니다
      </p>

      {rows.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-10 text-center text-sm text-slate-400">
          북마크한 공고가 없습니다. 공고 목록에서 🔖 를 누르면 여기에 모입니다.
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="pl-4 pr-1 py-3 w-12 text-right">No</th>
                <th className="px-4 py-3 text-left">공고명</th>
                <th className="px-4 py-3 text-right">추정가격</th>
                <th className="px-4 py-3 text-left">마감</th>
                <th className="px-4 py-3 text-left">북마크</th>
                <th className="px-4 py-3 w-40" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const at = r.snapshot?.closingAt;
                const closed = !!at && new Date(at) < new Date();
                const days = at && !closed ? Math.ceil((new Date(at).getTime() - Date.now()) / 86_400_000) : null;
                return (
                  <tr
                    key={r.id}
                    onClick={() => onOpen(r.bid ?? fromSnapshot(r))}
                    className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${closed ? 'text-slate-400' : ''}`}
                  >
                    <td className="pl-4 pr-1 py-3 text-right text-xs tabular-nums text-slate-400">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 flex items-center gap-1.5">
                        {r.bidNtceNm}
                        {r.hasSummary && <Sparkles size={13} className="text-indigo-500 flex-shrink-0" aria-label="AI 요약 있음" />}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{r.clientName} · {r.bidNtceNo}</div>
                      {r.memo && <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-line">{r.memo}</div>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap">{fmtPrice(r.snapshot?.presmptPrce)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {closed ? (
                        <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-600 text-xs font-medium">마감 지남</span>
                      ) : days != null ? (
                        <span className={`text-xs font-semibold ${days <= 3 ? 'text-red-600' : 'text-slate-700'}`}>D-{days}</span>
                      ) : null}
                      <div className="text-xs text-slate-400">{r.snapshot?.closingKind ?? '마감'} {fmtDate(at)}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                      {r.createdBy ?? '-'}
                      <div className="text-slate-400">{fmtDate(r.createdAt).slice(0, 10)}</div>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {!closed && r.bid && (
                          <button
                            disabled={busy === r.id}
                            onClick={() => act(r, `/bidding/${encodeURIComponent(r.bidNtceNo)}/take`)}
                            className="whitespace-nowrap rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                          >
                            프로젝트로
                          </button>
                        )}
                        {/* 되돌리기 어려운 동작은 한 번 더 누른다(#108). 해제는 북마크만 지우고, 미참여는 「다시 담기」로 되살린다 */}
                        <ConfirmButton
                          disabled={busy === r.id}
                          onConfirm={() => act(r, `/bidding/projects/${r.id}/bookmark`, 'DELETE')}
                          ask="한 번 더 — 북마크 해제"
                          armedClassName="whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-medium bg-slate-700 text-white"
                          className="whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 disabled:opacity-40"
                        >
                          해제
                        </ConfirmButton>
                        <ConfirmButton
                          disabled={busy === r.id}
                          onConfirm={() => act(r, `/bidding/projects/${r.id}/skip`)}
                          ask="한 번 더 — 미참여"
                          className="whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 disabled:opacity-40"
                        >
                          미참여
                        </ConfirmButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
